<ra-square-icon
    backgroundColor="ghost"
    [icon]="icon()"
    [size]="48"
    [iconSize]="22"
    [active]="isOpen()"
    [class.default-disabled]="disabled() && !isOpen()"
    [ariaLabel]="labelKey() | translate"
    [raEditTooltip]="isOpen() ? null : { text: (labelKey() | translate), position: 'left' }"
    (click)="toggle()"
/>

<!-- Conteúdo do painel da direita (media-look-panel): desenhado lá, ligado a este card.
     Uma coluna só: o painel tem a altura da tela, e a foto fica à vista ao lado. -->
<ng-template #panel>
    @let current = size();
    @let blocked = disabled();

    <div class="look-panel-body">
        @switch (kind()) {
            @case ('group') {
                <p class="look-title">{{ 'document.edit.railLook.photosPerRow' | translate }}</p>
                <div class="look-grid">
                    @for (count of columnOptions; track count) {
                        <button
                            type="button"
                            class="look-option"
                            [class.current]="columns() === count"
                            [disabled]="blocked"
                            (click)="chooseColumns.emit(count)">
                            <look-glyph [name]="colsGlyph(count)" />
                            <span>{{ count }}</span>
                        </button>
                    }
                </div>
            }
            @default {
                <p class="look-title">
                    {{ (kind() === 'video' ? 'document.edit.railLook.thumbnailSize' : 'document.edit.imageCard.size') | translate }}
                </p>
                <div class="look-grid">
                    @for (preset of presets; track preset.grid) {
                        <button
                            type="button"
                            class="look-option"
                            [class.current]="current.width === null && current.grid === preset.grid"
                            [disabled]="blocked"
                            (click)="selectPreset(preset.grid)">
                            <look-glyph [name]="preset.glyph" />
                            <span>{{ preset.labelKey | translate }}</span>
                        </button>
                    }
                </div>

                <!-- Largura livre, digitável: só a foto (o vídeo segue os prontos). -->
                @if (kind() === 'image') {
                    <p class="look-subtitle">{{ 'document.edit.imageCard.sizeCustom' | translate }}</p>
                    <div class="look-field">
                        <image-width-field
                            [value]="width()"
                            [active]="current.width !== null"
                            [disabled]="blocked"
                            (valueChange)="onWidth($event)"
                            (commit)="flush()"
                        />
                    </div>
                }
            }
        }

        <span class="look-divider"></span>
        <p class="look-title">{{ 'document.edit.railLook.framing' | translate }}</p>
        <div class="look-grid" style="--look-cols: 2">
            <button
                type="button"
                class="look-option"
                [class.current]="!fullImage()"
                [disabled]="blocked"
                (click)="fullImage() && fullImageChange.emit(false)">
                <look-glyph name="fit-fill" />
                <span>{{ 'document.edit.railLook.framingFill' | translate }}</span>
            </button>
            <button
                type="button"
                class="look-option"
                [class.current]="fullImage()"
                [disabled]="blocked"
                (click)="!fullImage() && fullImageChange.emit(true)">
                <look-glyph name="fit-full" />
                <span>{{ 'document.edit.railLook.framingFull' | translate }}</span>
            </button>
        </div>

        <!-- Cor do fundo: sempre à vista (pedido do usuário), qualquer que seja o tamanho,
             o enquadramento ou a posição da descrição. -->
        <p class="look-subtitle">{{ 'document.edit.mediaFill.title' | translate }}</p>
        <media-fill-field
            [value]="fillColor()"
            [disabled]="blocked"
            (preview)="fillPreview.emit($event)"
            (choose)="chooseFill($event)"
        />

        @if (kind() === 'video') {
            <span class="look-divider"></span>
            <button
                type="button"
                class="look-switch"
                role="switch"
                [attr.aria-checked]="showVideo()"
                [disabled]="blocked"
                (click)="showVideoChange.emit(!showVideo())">
                <span>
                    {{ 'document.edit.railLook.showInPdf' | translate }}
                    <small>{{ 'document.edit.railLook.showInPdfHint' | translate }}</small>
                </span>
                <span class="look-toggle" [class.on]="showVideo()" aria-hidden="true"></span>
            </button>
        }

        <!-- Legenda: como a descrição se prende à mídia e, na mídia única, onde ela fica. -->
        <span class="look-divider"></span>
        <p class="look-title">{{ 'document.edit.railLook.caption' | translate }}</p>
        <div class="look-grid" style="--look-cols: 2">
            @for (option of captionStyles; track option.style) {
                <button
                    type="button"
                    class="look-option"
                    [class.current]="captionStyle() === option.style"
                    [disabled]="blocked"
                    (click)="captionStyle() !== option.style && captionStyleChange.emit(option.style)">
                    <look-glyph [name]="option.glyph" />
                    <span>{{ option.labelKey | translate }}</span>
                </button>
            }
        </div>
        @if (kind() !== 'group') {
            <!-- Estilo e posição são ajustes distintos: o subtítulo separa os dois pares. -->
            <p class="look-subtitle">{{ 'document.edit.railLook.captionPosition' | translate }}</p>
            <div class="look-grid" style="--look-cols: 2">
                <button
                    type="button"
                    class="look-option"
                    [class.current]="!captionSide()"
                    [disabled]="blocked"
                    (click)="captionSide() && captionSideChange.emit(false)">
                    <look-glyph name="place-below" />
                    <span>{{ 'document.edit.railLook.captionBelow' | translate }}</span>
                </button>
                <button
                    type="button"
                    class="look-option"
                    [class.current]="captionSide()"
                    [disabled]="blocked"
                    (click)="!captionSide() && captionSideChange.emit(true)">
                    <look-glyph name="place-side" />
                    <span>{{ 'document.edit.railLook.captionSide' | translate }}</span>
                </button>
            </div>
            @if (captionSide()) {
                <p class="look-hint">{{ 'document.edit.railLook.captionSideHint' | translate }}</p>
            }
        }

        <!-- O que a foto mostra: na foto única, número e data abrem a legenda. -->
        @if (kind() !== 'video') {
            <span class="look-divider"></span>
            <p class="look-title">
                {{ (kind() === 'group' ? 'document.edit.railLook.showOnPhotos' : 'document.edit.railLook.showOnPhoto') | translate }}
            </p>
            <div class="look-chips">
                <button
                    type="button"
                    class="look-chip"
                    [class.current]="showNumber()"
                    [attr.aria-pressed]="showNumber()"
                    [disabled]="blocked"
                    (click)="showNumberChange.emit(!showNumber())">
                    @if (showNumber()) {
                        <ra-icon icon="check" varColor="currentColor" [size]="12" />
                    }
                    <b aria-hidden="true">#</b>
                    <span>{{ 'document.edit.railLook.showNumber' | translate }}</span>
                </button>
                <button
                    type="button"
                    class="look-chip"
                    [class.current]="showDate()"
                    [attr.aria-pressed]="showDate()"
                    [disabled]="blocked"
                    (click)="showDateChange.emit(!showDate())">
                    @if (showDate()) {
                        <ra-icon icon="check" varColor="currentColor" [size]="12" />
                    }
                    <ra-icon icon="calendar" varColor="currentColor" [size]="13" />
                    <span>{{ 'document.edit.railLook.showDate' | translate }}</span>
                </button>
            </div>
        }

        @if (canRestore()) {
            <span class="look-divider"></span>
            <button type="button" class="look-link" [disabled]="blocked" (click)="onRestore()">
                {{ 'document.edit.railLook.restoreDefaults' | translate }}
            </button>
        }
    </div>
</ng-template>
